import {
  AmountType,
  CalculateByField,
  ConditionField,
  DimensionDescriptor,
  FieldConfiguration,
  FieldConfigurationType,
  IrrCashFlowFrequency,
  MeasureDateRange,
  MeasureDescriptor,
  MeasureUnit,
} from "../../../../../../shared/reporting/api/biClient.types";
import cloneDeep from "../../../../../../shared/utilities/cloneDeep";
import { generateGuid } from "../../../../../../shared/utilities/generateGuid";
import { CustomMeasureDescription, CustomMeasureUnit } from "../../../../../api/biApi.types";
import { createFieldId } from "../../utilities/createFieldId";
import { CustomMeasureState } from "../types/customMeasure.types";
import { FormulaNode } from "./fillNodes";
import { normalizeVariableName } from "./tokenizer";

export const convertToMeasure = (
  state: CustomMeasureState,
  basedMeasure?: MeasureDescriptor
): CustomMeasureDescription => {
  const calculateBy = state.calculateBy || CalculateByField.Lcy;
  const amountType = state.amountType || AmountType.Net;
  const units = buildUnits(state, calculateBy, amountType);
  const name = basedMeasure?.name || generateMeasureName(state.caption ?? "");
  const guid = basedMeasure?.configuration?.guid || generateGuid();

  const measure: CustomMeasureDescription = {
    name,
    caption: `${state.caption}`,
    formula: `${state.formula}`,
    calculateBy: calculateBy,
    amountType,
    group: `${state.group}`,
    units,
    configuration: { ...buildConfiguration(state), name, guid },
  };

  return measure;
};

const buildUnits = (state: CustomMeasureState, calculateBy: CalculateByField, amountType: AmountType) => {
  const units: CustomMeasureUnit[] = [];
  state.formulaNodes.forEach((fn) => buildUnit(units, fn, calculateBy, amountType));
  return units;
};

const buildUnit = (
  units: CustomMeasureUnit[],
  node: FormulaNode,
  calculateBy: CalculateByField,
  amountType: AmountType
) => {
  if (node.function !== undefined) {
    node.args?.forEach((arg) => buildUnit(units, arg, calculateBy, amountType));
  } else {
    const unit = { variable: node.value } as CustomMeasureUnit;

    unit.unit = {
      measureBasedUnit: false,
      filters: node.conditions?.map((c) => cloneDeep(c.config.filter)),
      table: node.table,
      calculateByField: calculateBy,
      amountType,
    };

    units.push(unit);
  }
};

export const toAmountCalculateByCaption = (calcBy: CalculateByField, amountType: AmountType): string => {
  switch (calcBy) {
    case CalculateByField.Lcy:
      return `${toAmountTypeCaption(amountType)} (LCY)`;
    case CalculateByField.Pcy:
      return `${toAmountTypeCaption(amountType)} (PCY)`;
    case CalculateByField.AdditionalLcy:
      return `Additional-Currency ${toAmountTypeCaption(amountType)}`;
    case CalculateByField.Shares:
      return "Shares";
    default:
      return "";
  }
};

const toAmountTypeCaption = (amountType: AmountType): string => {
  switch (amountType) {
    case AmountType.Net:
      return "Amount";
    case AmountType.Credit:
      return "Credit Amount";
    case AmountType.Debit:
      return "Debit Amount";
    default:
      return "";
  }
};

export const toAmountTypeControlCaption = (amountType: AmountType): string => {
  switch (amountType) {
    case AmountType.Net:
      return "Net";
    case AmountType.Credit:
      return "Credit";
    case AmountType.Debit:
      return "Debit";
    default:
      return "";
  }
};

export const toFormulaNodes = (
  units: { [key: string]: MeasureUnit },
  dimensions: DimensionDescriptor[]
): FormulaNode[] => {
  const result: FormulaNode[] = [];

  for (const variable in units) {
    const unit = units[variable];
    if (unit === undefined) continue;

    if (unit.filters !== undefined) {
      const formulaNode: FormulaNode = {
        key: variable.toLowerCase(),
        text: variable,
        value: normalizeVariableName(variable),
        conditions: unit.filters.map(
          (filter) =>
            ({
              meta: cloneDeep(dimensions.find((d) => d.name === filter.dimensionName)),
              config: { filter: cloneDeep(filter) },
            }) as ConditionField
        ),
        linkedNodes: [],
        table: unit.table,
      };
      result.push(formulaNode);
    }
  }
  return result;
};

const buildConfiguration = (state: CustomMeasureState): FieldConfiguration => {
  const hasIrrFunction = state.formulaNodes.some((fn) => fn.function?.name === "XIRR");
  if (hasIrrFunction) {
    return <FieldConfiguration>{
      type: FieldConfigurationType.Irr,
      dateRange: MeasureDateRange.EndingBalance,
      cashFlowFrequency: IrrCashFlowFrequency.Daily,
      format: state.format,
    };
  }
  return <FieldConfiguration>{
    type: FieldConfigurationType.Measure,
    dateRange: MeasureDateRange.EndingBalance,
    format: state.format,
  };
};

export const generateMeasureName = (caption: string): string => {
  const onlyLetters = caption.replace(/[^a-zA-Z]/g, "");
  return createFieldId(onlyLetters);
};
